import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Calculator, TrendingUp, Info, Target, AlertTriangle } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Cell } from "recharts";

const STORAGE_KEY = 'lumpSum_data';

export const LumpSumCalculator = () => {
  // State for inputs
  const [investment, setInvestment] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).investment : 50000;
  });
  
  const [expectedReturn, setExpectedReturn] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).expectedReturn : 12;
  });
  
  const [years, setYears] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).years : 10;
  });
  
  const [inflationRate, setInflationRate] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).inflationRate : 3;
  });

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      investment,
      expectedReturn,
      years,
      inflationRate
    }));
  }, [investment, expectedReturn, years, inflationRate]);

  // Calculate lump sum growth
  const calculateLumpSum = () => {
    const p = parseFloat(investment) || 0;
    const r = parseFloat(expectedReturn) / 100 || 0;
    const t = parseFloat(years) || 0;
    const inflation = parseFloat(inflationRate) / 100 || 0;

    const futureValue = p * Math.pow(1 + r, t);
    const totalGains = futureValue - p;
    const cagr = ((Math.pow(futureValue / p, 1 / t) - 1) * 100);
    
    // Real return adjusted for inflation
    const realReturn = ((1 + r) / (1 + inflation) - 1) * 100;
    const inflationAdjustedValue = p * Math.pow(1 + (r - inflation), t);

    return {
      futureValue,
      totalGains,
      cagr,
      principal: p,
      realReturn,
      inflationAdjustedValue
    };
  };

  // Generate year-wise growth
  const generateYearWiseData = () => {
    const p = parseFloat(investment) || 0;
    const r = parseFloat(expectedReturn) / 100 || 0;
    const t = parseFloat(years) || 0;
    const inflation = parseFloat(inflationRate) / 100 || 0;

    const data = [];
    for (let year = 0; year <= t; year++) {
      const nominal = p * Math.pow(1 + r, year);
      const real = p * Math.pow(1 + (r - inflation), year);
      const gains = nominal - p;
      
      data.push({
        year: year,
        value: Math.round(nominal),
        realValue: Math.round(real),
        gains: Math.round(gains)
      });
    }
    return data;
  };

  // Scenario analysis (Low, Expected, High returns)
  const generateScenarios = () => {
    const p = parseFloat(investment) || 0;
    const r = parseFloat(expectedReturn) / 100 || 0;
    const t = parseFloat(years) || 0;

    const lowReturn = r * 0.7;  // 30% lower
    const highReturn = r * 1.3; // 30% higher

    const data = [];
    for (let year = 0; year <= t; year++) {
      data.push({
        year: year,
        low: Math.round(p * Math.pow(1 + lowReturn, year)),
        expected: Math.round(p * Math.pow(1 + r, year)),
        high: Math.round(p * Math.pow(1 + highReturn, year))
      });
    }
    return data;
  };

  // Risk-return analysis
  const getRiskReturnData = () => {
    const scenarios = [
      { name: 'Conservative (6%)', return: 6, risk: 2 },
      { name: 'Moderate (10%)', return: 10, risk: 4 },
      { name: 'Balanced (12%)', return: 12, risk: 5 },
      { name: 'Aggressive (15%)', return: 15, risk: 7 },
      { name: 'High Risk (20%)', return: 20, risk: 9 }
    ];

    return scenarios.map(s => ({
      ...s,
      value: Math.round((parseFloat(investment) || 0) * Math.pow(1 + s.return / 100, parseFloat(years) || 0))
    }));
  };

  // Compare lump sum vs SIP
  const compareLumpSumVsSIP = () => {
    const p = parseFloat(investment) || 0;
    const r = parseFloat(expectedReturn) / 100 / 12 || 0; // Monthly return
    const t = parseFloat(years) || 0;
    const months = t * 12;

    // Lump sum
    const lumpSumFV = p * Math.pow(1 + r * 12, t);

    // SIP (Monthly contribution = investment / total months)
    const monthlySIP = p / months;
    let sipFV = 0;
    for (let month = 0; month < months; month++) {
      sipFV += monthlySIP * Math.pow(1 + r, months - month);
    }

    const data = [];
    for (let year = 0; year <= t; year++) {
      const currentMonths = year * 12;
      const lumpValue = p * Math.pow(1 + r * 12, year);
      
      let sipValue = 0;
      for (let month = 0; month < currentMonths; month++) {
        sipValue += monthlySIP * Math.pow(1 + r, currentMonths - month);
      }

      data.push({
        year: year,
        lumpSum: Math.round(lumpValue),
        sip: Math.round(sipValue)
      });
    }
    return data;
  };

  const results = calculateLumpSum();
  const yearWiseData = generateYearWiseData();
  const scenarios = generateScenarios();
  const riskReturnData = getRiskReturnData();
  const lumpSumVsSIP = compareLumpSumVsSIP();

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))'
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      {/* Navigation */}
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[hsl(var(--accent))] flex items-center justify-center">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Lump Sum Investment Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          {/* Horizontal Scrollable Tabs */}
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-[hsl(var(--accent))] data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-[hsl(var(--accent))] data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-[hsl(var(--accent))] data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Tab 1: Calculator Overview & Results */}
          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            {/* Introduction */}
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is Lump Sum Investment?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  A lump sum investment means investing a large amount of money all at once, rather than gradually over time. 
                  This calculator helps you project the future value of your one-time investment, understand potential returns 
                  (CAGR), and compare different scenarios to make informed investment decisions.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Input Section */}
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Input Parameters</CardTitle>
                  <CardDescription>Enter your investment details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="investment" className="text-sm font-medium">
                      Lump Sum Amount ($)
                    </Label>
                    <Input
                      id="investment"
                      type="number"
                      value={investment}
                      onChange={(e) => setInvestment(e.target.value)}
                      placeholder="50000"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="expectedReturn" className="text-sm font-medium">
                      Expected Annual Return (%)
                    </Label>
                    <Input
                      id="expectedReturn"
                      type="number"
                      value={expectedReturn}
                      onChange={(e) => setExpectedReturn(e.target.value)}
                      placeholder="12"
                      step="0.1"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="years" className="text-sm font-medium">
                      Investment Duration (Years)
                    </Label>
                    <Input
                      id="years"
                      type="number"
                      value={years}
                      onChange={(e) => setYears(e.target.value)}
                      placeholder="10"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="inflationRate" className="text-sm font-medium">
                      Inflation Rate (%)
                    </Label>
                    <Input
                      id="inflationRate"
                      type="number"
                      value={inflationRate}
                      onChange={(e) => setInflationRate(e.target.value)}
                      placeholder="3"
                      step="0.1"
                      className="text-base"
                    />
                  </div>

                  <Button 
                    className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))] text-white shadow-md"
                    onClick={() => setInvestment(investment)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate
                  </Button>
                </CardContent>
              </Card>

              {/* Results Section */}
              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Investment Projection Results</CardTitle>
                    <CardDescription>Your lump sum investment growth forecast</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-[hsl(var(--accent-light))] to-white border border-[hsl(var(--accent))]/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--accent))] flex items-center justify-center">
                            <Target className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Future Portfolio Value</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-[hsl(var(--accent))]">
                          {formatCurrency(results.futureValue)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-[hsl(var(--primary-light))] to-white border border-[hsl(var(--primary))]/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary))] flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Gains</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-[hsl(var(--primary))]">
                          {formatCurrency(results.totalGains)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-[hsl(var(--secondary-light))] to-white border border-[hsl(var(--secondary))]/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">CAGR (Annual Growth)</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-[hsl(var(--secondary))]">
                          {results.cagr.toFixed(2)}%
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-[hsl(var(--purple-light))] to-white border border-[hsl(var(--purple))]/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--purple))] flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Inflation-Adjusted Value</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-[hsl(var(--purple))]">
                          {formatCurrency(results.inflationAdjustedValue)}
                        </p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="p-4 rounded-lg bg-muted/50">
                        <p className="text-sm font-medium text-muted-foreground mb-1">Initial Investment</p>
                        <p className="text-2xl font-bold text-foreground">{formatCurrency(results.principal)}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-muted/50">
                        <p className="text-sm font-medium text-muted-foreground mb-1">Real Return (After Inflation)</p>
                        <p className="text-2xl font-bold text-foreground">{results.realReturn.toFixed(2)}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Year-wise Growth Table */}
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Year-wise Investment Growth</CardTitle>
                    <CardDescription>Track your investment value over time (nominal and inflation-adjusted)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-4 font-semibold text-foreground">Year</th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground">Portfolio Value</th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground">Total Gains</th>
                            <th className="text-right py-3 px-4 font-semibold text-foreground">Real Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {yearWiseData.map((row, index) => (
                            <tr 
                              key={index} 
                              className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                            >
                              <td className="py-3 px-4 font-medium">{row.year}</td>
                              <td className="py-3 px-4 text-right text-[hsl(var(--accent))]">{formatCurrency(row.value)}</td>
                              <td className="py-3 px-4 text-right text-[hsl(var(--primary))]">{formatCurrency(row.gains)}</td>
                              <td className="py-3 px-4 text-right font-semibold text-[hsl(var(--purple))]">{formatCurrency(row.realValue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Tab 2: Visual Analysis (Charts) */}
          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Portfolio Growth Over Time</CardTitle>
                <CardDescription className="text-base">
                  Visualize how your lump sum investment grows year by year
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={yearWiseData}>
                      <defs>
                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--accent))" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="year" 
                        label={{ value: 'Year', position: 'insideBottom', offset: -5 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        label={{ value: 'Value ($)', angle: -90, position: 'insideLeft' }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="value" 
                        stroke="hsl(var(--accent))" 
                        fill="url(#colorValue)"
                        strokeWidth={3}
                        name="Portfolio Value"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Scenario Analysis</CardTitle>
                  <CardDescription>
                    Compare outcomes with different return rates (Low / Expected / High)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={scenarios}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="year" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="low" 
                          stroke="hsl(var(--destructive))" 
                          strokeWidth={2}
                          name="Low Return (30% less)"
                          dot={false}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="expected" 
                          stroke="hsl(var(--primary))" 
                          strokeWidth={3}
                          name="Expected Return"
                          dot={false}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="high" 
                          stroke="hsl(var(--accent))" 
                          strokeWidth={2}
                          name="High Return (30% more)"
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Nominal vs Inflation-Adjusted Returns</CardTitle>
                  <CardDescription>
                    See the impact of inflation on your real purchasing power
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={yearWiseData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="year" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="value" 
                          stroke="hsl(var(--accent))" 
                          strokeWidth={3}
                          name="Nominal Value"
                          dot={false}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="realValue" 
                          stroke="hsl(var(--purple))" 
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          name="Real Value (Inflation-Adjusted)"
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="font-display">Lump Sum vs SIP Comparison</CardTitle>
                <CardDescription className="text-base">
                  Compare lump sum investment vs systematic investment plan (same total amount spread over time)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={lumpSumVsSIP}>
                      <defs>
                        <linearGradient id="colorLumpSum" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorSIP" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--secondary))" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(var(--secondary))" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="year" 
                        label={{ value: 'Year', position: 'insideBottom', offset: -5 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        label={{ value: 'Portfolio Value ($)', angle: -90, position: 'insideLeft' }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="lumpSum" 
                        stroke="hsl(var(--primary))" 
                        fill="url(#colorLumpSum)"
                        strokeWidth={3}
                        name="Lump Sum"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="sip" 
                        stroke="hsl(var(--secondary))" 
                        fill="url(#colorSIP)"
                        strokeWidth={2}
                        name="SIP (Monthly)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="font-display">Risk-Return Profile</CardTitle>
                <CardDescription className="text-base">
                  Compare different investment strategies based on risk and potential returns
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={riskReturnData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        type="number"
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <YAxis 
                        type="category"
                        dataKey="name" 
                        stroke="hsl(var(--muted-foreground))"
                        width={150}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Bar dataKey="value" name="Final Value">
                        {riskReturnData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 3: Explanation, Risks & Insights */}
          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Your Investment Results</CardTitle>
                <CardDescription className="text-base">
                  Deep insights into what these numbers mean for your financial future
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--accent))]">
                    What is CAGR?
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    CAGR (Compound Annual Growth Rate) represents the average annual growth rate of your investment over time. 
                    Your investment's CAGR is <strong className="text-[hsl(var(--accent))]">{results.cagr.toFixed(2)}%</strong>, 
                    which matches your expected return because lump sum investments compound at a consistent rate.
                  </p>
                  <div className="p-4 rounded-lg bg-muted/50 text-sm">
                    <p className="font-mono mb-2">CAGR = [(Final Value / Initial Value)^(1/Years) - 1] × 100</p>
                    <p className="font-mono">
                      CAGR = [({formatCurrency(results.futureValue)} / {formatCurrency(results.principal)})^(1/{years}) - 1] × 100 = {results.cagr.toFixed(2)}%
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--primary))]">
                    Where Your Profits Come From
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Your initial investment of {formatCurrency(results.principal)} will grow to {formatCurrency(results.futureValue)} 
                    over {years} years at {expectedReturn}% annual return.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-[hsl(var(--accent-light))] border border-[hsl(var(--accent))]/20">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Your Investment</p>
                      <p className="text-2xl font-bold text-[hsl(var(--accent))]">
                        {formatCurrency(results.principal)}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Initial lump sum
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-[hsl(var(--primary-light))] border border-[hsl(var(--primary))]/20">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Investment Gains</p>
                      <p className="text-2xl font-bold text-[hsl(var(--primary))]">
                        {formatCurrency(results.totalGains)}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Profit from growth
                      </p>
                    </div>
                  </div>
                  <p className="text-base text-muted-foreground leading-relaxed mt-4">
                    That's a <strong className="text-[hsl(var(--primary))]">
                      {((results.totalGains / results.principal) * 100).toFixed(1)}%
                    </strong> total return on your investment!
                  </p>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--secondary))]">
                    Time, Rate, and Risk: The Investment Triangle
                  </h3>
                  <div className="space-y-4 text-base text-muted-foreground leading-relaxed">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold text-foreground mb-2">⏰ Time Horizon Matters</h4>
                      <p>
                        The longer your investment period, the more time your money has to compound. With {years} years, 
                        you're giving your investment substantial time to grow through market cycles and benefit from 
                        long-term appreciation.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold text-foreground mb-2">📈 Return Rate Expectations</h4>
                      <p>
                        Your expected return of {expectedReturn}% annually is {
                          expectedReturn >= 15 ? 'aggressive and requires high-risk investments like individual stocks or emerging markets' :
                          expectedReturn >= 10 ? 'balanced and typical of diversified equity portfolios over long periods' :
                          'conservative and suitable for bond-heavy or lower-risk portfolios'
                        }.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold text-foreground mb-2">⚖️ Risk-Return Trade-off</h4>
                      <p>
                        Higher potential returns come with higher risk. The scenario analysis shows that if returns are 
                        30% lower than expected, your final value would be {formatCurrency(scenarios[scenarios.length - 1].low)} 
                        instead of {formatCurrency(scenarios[scenarios.length - 1].expected)}.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--purple))]">
                    The Inflation Reality Check
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    While your investment grows to {formatCurrency(results.futureValue)}, inflation erodes purchasing power. 
                    At {inflationRate}% annual inflation, the real value is {formatCurrency(results.inflationAdjustedValue)}.
                  </p>
                  <div className="p-6 rounded-xl bg-gradient-to-br from-[hsl(var(--purple-light))] to-white border border-[hsl(var(--purple))]/20">
                    <p className="text-sm font-medium text-muted-foreground mb-2">Real Return After Inflation</p>
                    <div className="grid sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-lg font-semibold text-foreground">Nominal Return</p>
                        <p className="text-3xl font-display font-bold text-[hsl(var(--accent))]">
                          {expectedReturn}%
                        </p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-foreground">Real Return</p>
                        <p className="text-3xl font-display font-bold text-[hsl(var(--purple))]">
                          {results.realReturn.toFixed(2)}%
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Inflation reduces your effective annual return by {(parseFloat(expectedReturn) - results.realReturn).toFixed(2)} percentage points
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--secondary))]">
                    Lump Sum vs SIP: When to Choose What
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    The comparison shows that lump sum typically outperforms SIP in rising markets because all money is invested 
                    and compounding from day one. However, market timing risk is higher.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-[hsl(var(--primary-light))] border-l-4 border-[hsl(var(--primary))]">
                      <h4 className="font-semibold text-foreground mb-2">✅ Choose Lump Sum When:</h4>
                      <ul className="text-sm text-muted-foreground space-y-2">
                        <li>• Markets are trending upward</li>
                        <li>• You have a windfall (bonus, inheritance)</li>
                        <li>• You have high risk tolerance</li>
                        <li>• Long investment horizon (10+ years)</li>
                      </ul>
                    </div>
                    <div className="p-4 rounded-lg bg-[hsl(var(--secondary-light))] border-l-4 border-[hsl(var(--secondary))]">
                      <h4 className="font-semibold text-foreground mb-2">✅ Choose SIP When:</h4>
                      <ul className="text-sm text-muted-foreground space-y-2">
                        <li>• Markets are volatile or peaking</li>
                        <li>• You don't have lump sum available</li>
                        <li>• You prefer Dollar cost averaging</li>
                        <li>• You want disciplined investing</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--destructive))]">
                    Important Risks and Assumptions
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-[hsl(var(--destructive))] font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Market Timing Risk:</strong> Lump sum investing exposes 
                        your entire capital to market conditions at once. If you invest at a market peak, short-term 
                        losses can be significant.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-[hsl(var(--destructive))] font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Return Assumptions:</strong> This calculator assumes 
                        consistent {expectedReturn}% returns. Real markets are volatile with years of gains and losses.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-[hsl(var(--destructive))] font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">No Additional Contributions:</strong> These projections 
                        assume no additional investments. Regular contributions would significantly boost returns.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-[hsl(var(--destructive))] font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Taxes and Fees:</strong> Investment returns may be reduced 
                        by capital gains taxes, transaction fees, and fund management charges.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-[hsl(var(--destructive))] font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Sequence of Returns Risk:</strong> The order of returns 
                        matters. Poor returns early in your investment period can significantly impact final outcomes.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-[hsl(var(--accent))]">
                    Practical Investment Strategies
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-[hsl(var(--primary-light))] border-l-4 border-[hsl(var(--primary))]">
                      <h4 className="font-semibold text-foreground mb-2">✅ Dollar Cost Averaging with Lump Sum</h4>
                      <p className="text-muted-foreground">
                        Instead of investing all at once, consider dividing your lump sum into 6-12 monthly investments 
                        to reduce timing risk while still benefiting from earlier investment.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-[hsl(var(--accent-light))] border-l-4 border-[hsl(var(--accent))]">
                      <h4 className="font-semibold text-foreground mb-2">✅ Diversification is Key</h4>
                      <p className="text-muted-foreground">
                        Spread your lump sum across different asset classes (stocks, bonds, real estate) and geographies 
                        to reduce risk without sacrificing returns.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-[hsl(var(--secondary-light))] border-l-4 border-[hsl(var(--secondary))]">
                      <h4 className="font-semibold text-foreground mb-2">✅ Rebalance Regularly</h4>
                      <p className="text-muted-foreground">
                        Review and rebalance your portfolio annually to maintain your target asset allocation and 
                        lock in gains from outperforming assets.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-[hsl(var(--purple-light))] border-l-4 border-[hsl(var(--purple))]">
                      <h4 className="font-semibold text-foreground mb-2">✅ Stay Invested Long-Term</h4>
                      <p className="text-muted-foreground">
                        The charts show returns accelerate over time. Avoid panic selling during market downturns - 
                        staying invested through volatility is crucial for achieving projected returns.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default LumpSumCalculator;
